import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import {
  ValuationRequest,
  ValuationRequestPublic,
  ValuationRequestAuthenticated,
  ValuationResponse,
  VehicleEstimationRequest,
  VehicleEstimationResponse,
  AutoUncleRatingIntervals,
  RatingIntervalsRefreshResult,
} from '@/types/valuation';

interface AutoUncleCarResponse {
  car: {
    year: number;
    brand: string;
    car_model: string;
    hp?: number;
    body?: string;
    lpn: string;
    vin: string;
    fuel?: string;
    engineVolume?: string;
    color?: string;
    [key: string]: any;
  };
}

export class ValuationService {
  /**
   * Get vehicle information (guest public lookup).
   * GET /valuation/vehicle-info/:lpn — requires Cloudflare Turnstile token.
   */
  static async getVehicleInformation(
    lpn: string,
    turnstileToken?: string,
  ): Promise<AutoUncleCarResponse> {
    const response = await api.get<{
      success: true;
      data: AutoUncleCarResponse;
    }>(`/valuation/vehicle-info/${encodeURIComponent(lpn)}`, {
      // Do NOT use cf-turnstile-response: Cloudflare in front of api.bilenia.se
      // strips client cf-* headers, so the token never reaches Express in prod.
      params: turnstileToken ? { turnstileToken } : undefined,
      headers: turnstileToken
        ? { "x-turnstile-token": turnstileToken }
        : undefined,
    });
    return response.data;
  }

  /**
   * Get vehicle registration date from Smart365
   * GET /valuation/registration-date/:lpn
   */
  static async getRegistrationDate(lpn: string): Promise<string | null> {
    const response = await api.get<{
      success: true;
      data: { registrationDate: string | null };
    }>(`/valuation/registration-date/${encodeURIComponent(lpn)}`);
    return response.data.registrationDate;
  }

  /**
   * Create a new vehicle valuation for authenticated users
   * POST /valuation
   */
  static async createValuation(valuationData: ValuationRequestAuthenticated): Promise<VehicleEstimationResponse> {
    const response = await api.post<{
      success: true;
      data: VehicleEstimationResponse;
    }>('/valuation', valuationData);
    return response.data;
  }

  /**
   * Create a new vehicle valuation for public users
   * POST /valuation/public
   */
  static async createValuationPublic(valuationData: ValuationRequestPublic): Promise<VehicleEstimationResponse> {
    const response = await api.post<{
      success: true;
      data: VehicleEstimationResponse;
    }>('/valuation/public', valuationData);
    return response.data;
  }

  /**
   * Get vehicle price estimation from AutoUncle
   * POST /auto-uncle/vehicle-estimation
   */
  static async getVehicleEstimation(
    request: VehicleEstimationRequest
  ): Promise<VehicleEstimationResponse> {
    const response = await api.post<{
      success: true;
      data: VehicleEstimationResponse;
    }>('/auto-uncle/vehicle-estimation', request);
    return response.data;
  }

  /**
   * Fetch rating intervals for a vehicle card (admin "Uppdatera priser").
   * POST /admin/vehicle-cards/:id/rating-intervals/fetch
   * No body: the backend builds the AutoUncle request from the STORED row (sync plan
   * 2026-09-08), so the browser can no longer send a payload that differs from it.
   */
  static async fetchVehicleCardRatingIntervals(
    vehicleCardId: string
  ): Promise<RatingIntervalsRefreshResult> {
    const response = await api.post<{
      success: boolean;
      data: AutoUncleRatingIntervals;
      // Siblings of `data`, present only when the ladder returned a price.
      estimatedMarketValue?: number;
      purchasePrice?: number;
    }>(`/admin/vehicle-cards/${vehicleCardId}/rating-intervals/fetch`, {});
    return {
      ratingIntervals: response.data,
      estimatedMarketValue: response.estimatedMarketValue,
      purchasePrice: response.purchasePrice,
    };
  }

  /**
   * Fetch rating intervals for a lead (admin "Uppdatera priser").
   * POST /admin/leads/:id/rating-intervals/fetch
   * No body: the backend builds the AutoUncle request from the STORED row (sync plan
   * 2026-09-08), so the browser can no longer send a payload that differs from it.
   */
  static async fetchLeadRatingIntervals(
    leadId: string
  ): Promise<RatingIntervalsRefreshResult> {
    const response = await api.post<{
      success: boolean;
      data: AutoUncleRatingIntervals;
      // Siblings of `data`, present only when the ladder returned a price.
      estimatedMarketValue?: number;
      purchasePrice?: number;
    }>(`/admin/leads/${leadId}/rating-intervals/fetch`, {});
    return {
      ratingIntervals: response.data,
      estimatedMarketValue: response.estimatedMarketValue,
      purchasePrice: response.purchasePrice,
    };
  }
}
